import { formatFixed, parseFixed } from './fixed-decimal.js';
/** Storage scale of every money column (`numeric(20,4)`). */
export const MONEY_SCALE = 4;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;
export function parseCurrencyCode(value) {
    if (!CURRENCY_PATTERN.test(value)) {
        throw new RangeError(`"${value}" is not an ISO 4217 currency code`);
    }
    return value;
}
export class CurrencyMismatchError extends Error {
    name = 'CurrencyMismatchError';
    constructor(left, right) {
        super(`Cannot combine ${left} with ${right}; convert explicitly with a recorded FX rate`);
    }
}
/**
 * Immutable amount in a single currency, held as integer units at {@link MONEY_SCALE}.
 * Arithmetic across currencies is refused rather than silently converted.
 */
export class Money {
    units;
    currency;
    constructor(
    /** Amount in units of 10^-4 of the currency. */
    units, currency) {
        this.units = units;
        this.currency = currency;
    }
    static of(amount, currency) {
        return new Money(parseFixed(amount, MONEY_SCALE), parseCurrencyCode(currency));
    }
    static fromUnits(units, currency) {
        return new Money(units, currency);
    }
    static zero(currency) {
        return new Money(0n, currency);
    }
    static sum(items, currency) {
        return items.reduce((total, item) => total.add(item), Money.zero(currency));
    }
    add(other) {
        this.assertSameCurrency(other);
        return new Money(this.units + other.units, this.currency);
    }
    subtract(other) {
        this.assertSameCurrency(other);
        return new Money(this.units - other.units, this.currency);
    }
    negate() {
        return new Money(-this.units, this.currency);
    }
    compare(other) {
        this.assertSameCurrency(other);
        if (this.units === other.units)
            return 0;
        return this.units < other.units ? -1 : 1;
    }
    equals(other) {
        return this.currency === other.currency && this.units === other.units;
    }
    isZero() {
        return this.units === 0n;
    }
    isPositive() {
        return this.units > 0n;
    }
    isNegative() {
        return this.units < 0n;
    }
    /** Decimal string at storage scale, e.g. `"1250.5000"` — safe to pass to `numeric` columns. */
    toDecimalString() {
        return formatFixed(this.units, MONEY_SCALE);
    }
    toString() {
        return `${this.toDecimalString()} ${this.currency}`;
    }
    toJSON() {
        return { amount: this.toDecimalString(), currency: this.currency };
    }
    assertSameCurrency(other) {
        if (other.currency !== this.currency) {
            throw new CurrencyMismatchError(this.currency, other.currency);
        }
    }
}
//# sourceMappingURL=money.js.map